import React, { useState, useEffect, useRef } from 'react';
import {
  Sparkles, Search, Globe, Brain, Download, MessageCircle,
  Loader2, CheckCircle2, AlertCircle, Star, Layers, TrendingUp, Zap,
  BookOpen, MessageSquare, FileText, XCircle, AlertTriangle,
  ChevronDown, ChevronUp, RefreshCw, Info, Lock, Mail, Phone, ShieldCheck, X,
} from 'lucide-react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import toast from 'react-hot-toast';
import LandingSections from '../components/LandingSections';
import { useLandingEffects } from '../hooks/useLandingEffects';

const PYTHON_BASE =
  process.env.REACT_APP_PYTHON_API_URL || 'http://localhost:8000';

const PIPELINE_STEPS = [
  'Crawling website & metadata',
  'Querying ChatGPT, Gemini, Perplexity & Claude',
  'Scoring SEO, AEO, GEO & AI Visibility',
  'Analysing brand narratives & sentiment',
  'Benchmarking against competitors',
  'Compiling all 4 reports',
];

const HISTORY_KEY = 'nr_audit_history';
function saveToHistory(result) {
  const entry = {
    job_id: result.job_id,
    company_name: result.company_name,
    domain: result.domain,
    scores: result.scores,
    files: result.files,
    timestamp: new Date().toISOString(),
    company_profile:  result.company_profile,
    visibility_data:  result.visibility_data,
    seo_data:         result.seo_data,
    prompt_data:      result.prompt_data,
    narratives_data:  result.narratives_data,
  };
  const prev = getHistory();
  const next = [entry, ...prev.filter(h => h.job_id !== entry.job_id)].slice(0, 10);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
}
function getHistory() {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); } catch { return []; }
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function sc(v) {
  if (v >= 75) return '#16a34a';
  if (v >= 55) return '#0876C8';
  if (v >= 35) return '#E07B00';
  return '#3D1FCC';
}
function gradeBadgeClass(g) {
  if (!g) return 'badge-gray';
  const l = g.charAt(0);
  if (l === 'A') return 'badge-green';
  if (l === 'B') return 'badge-blue';
  if (l === 'C') return 'badge-amber';
  return 'badge-red';
}
function statusBadge(s) {
  if (s === 'Excellent') return 'badge-green';
  if (s === 'Good')      return 'badge-blue';
  if (s === 'Moderate')  return 'badge-amber';
  return 'badge-red';
}
function renderSafeValue(value) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map(v => renderSafeValue(v)).join(', ');
  if (typeof value === 'object') {
    if (value.content_recommendation) return renderSafeValue(value.content_recommendation);
    if (value.query) return renderSafeValue(value.query);
    return Object.entries(value).slice(0, 4).map(([k, v]) => `${k}: ${renderSafeValue(v)}`).join(' | ');
  }
  return String(value);
}

// ── Sub-components ────────────────────────────────────────────────────────────
function ScoreRing({ value, size = 100, stroke = 8, showPercent = false }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const fill = circ * (1 - value / 100);
  const color = sc(value);
  return (
    <div style={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--ink-100)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={fill} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4,0,0.2,1)' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontWeight: 800, fontSize: size > 90 ? 28 : 18, color, lineHeight: 1 }}>{value}{showPercent ? '%' : ''}</span>
        <span style={{ fontSize: 10, color: 'var(--ink-400)', marginTop: 2 }}>/ 100</span>
      </div>
    </div>
  );
}

function ScoreBar({ label, value, max }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
      <div style={{ width: 130, fontSize: 12, color: 'var(--ink-600)', flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: 7, background: 'var(--ink-100)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: sc(pct), borderRadius: 4, transition: 'width 1s ease' }} />
      </div>
      <div style={{ width: 36, textAlign: 'right', fontSize: 12, fontWeight: 700, color: sc(pct) }}>{value}/{max}</div>
    </div>
  );
}

const PLATFORM_META = {
  ChatGPT:    { color: '#10b981', bg: '#ecfdf5', logoUrl: 'https://www.google.com/s2/favicons?domain=chatgpt.com&sz=64' },
  Perplexity: { color: '#7c3aed', bg: '#ede9fe', logoUrl: 'https://www.google.com/s2/favicons?domain=perplexity.ai&sz=64' },
  Gemini:     { color: '#2563eb', bg: '#dbeafe', logoUrl: 'https://www.google.com/s2/favicons?domain=gemini.google.com&sz=64' },
  Claude:     { color: '#f59e0b', bg: '#fef3c7', logoUrl: 'https://www.google.com/s2/favicons?domain=claude.ai&sz=64' },
};

function PlatformLogo({ name, size = 28 }) {
  const [err, setErr] = useState(false);
  const meta = PLATFORM_META[name] || {};
  const fallback = { ChatGPT: '🤖', Perplexity: '🔍', Gemini: '✨', Claude: '🧠' }[name] || '🤖';
  if (!meta.logoUrl || err) return <span style={{ fontSize: size * 0.85, lineHeight: 1 }}>{fallback}</span>;
  return (
    <img src={meta.logoUrl} alt={name} width={size} height={size}
      style={{ borderRadius: 4, objectFit: 'contain', display: 'block' }}
      onError={() => setErr(true)} />
  );
}

function StatusPill({ val }) {
  const v = (val || '').toString().toLowerCase();
  const isPass = v === 'pass' || v.includes('pass');
  const isFail = v === 'fail' || v.includes('fail');
  return (
    <span className={`badge ${isPass ? 'badge-green' : isFail ? 'badge-red' : 'badge-amber'}`}
      style={{ fontSize: 11, whiteSpace: 'nowrap', minWidth: 68, justifyContent: 'center', display: 'inline-flex' }}>
      {isPass ? '✓' : isFail ? '✗' : '~'} {val}
    </span>
  );
}

async function _doDownload(jobId, file, companyName) {
  const res = await fetch(`${PYTHON_BASE}/download/${jobId}/${file}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const safe = (companyName || '').replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  const saveName = safe ? `${safe}_${file}` : file;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = saveName;
  a.click();
  URL.revokeObjectURL(a.href);
}

function DownloadBtn({ jobId, file, label, verifiedEmail, onNeedVerify, companyName }) {
  const [loading, setLoading] = useState(false);
  const go = async () => {
    if (loading) return;
    if (!verifiedEmail) { onNeedVerify?.(file); return; }
    setLoading(true);
    try {
      await _doDownload(jobId, file, companyName);
      toast.success(`Downloaded!`);
    } catch (e) {
      toast.error(e.message || 'Download failed');
    } finally {
      setLoading(false);
    }
  };
  return (
    <button onClick={go} disabled={loading} className="btn btn-primary btn-sm" style={{ display: 'inline-flex' }}>
      {loading
        ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />Downloading…</>
        : verifiedEmail
          ? <><Download size={14} />{label || `Download ${file.endsWith('.xlsx') ? 'XLSX' : 'PDF'}`}</>
          : <><Lock size={14} />Verify to Download</>}
    </button>
  );
}

// ── BlurGate ─────────────────────────────────────────────────────────────────
function BlurGate({ children, unlocked, onUnlock }) {
  if (unlocked) return <>{children}</>;
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ filter: 'blur(5px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.65 }}>
        {children}
      </div>
      <div style={{
        position: 'absolute', inset: 0, zIndex: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(250,247,242,0.55)', backdropFilter: 'blur(2px)',
        borderRadius: 'var(--r-lg)',
      }}>
        <div style={{ padding: '32px 40px', background: 'white', borderRadius: 'var(--r-lg)', boxShadow: 'var(--shadow-elegant)', textAlign: 'center', maxWidth: 360 }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#fff1f0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Lock size={24} color="var(--indigo)" />
          </div>
          <h3 className="font-display" style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Full analysis in your reports</h3>
          <p style={{ fontSize: 13, color: 'var(--ink-500)', lineHeight: 1.65, marginBottom: 20 }}>
            Verify your email & phone to download the complete reports and unlock all detailed analysis.
          </p>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={onUnlock}>
            <Download size={15} /> Verify & Download Reports
          </button>
        </div>
      </div>
    </div>
  );
}

// ── OtpModal ─────────────────────────────────────────────────────────────────
function OtpModal({ open, onClose, jobId, onVerified }) {
  const [step, setStep]             = useState(1);
  const [name, setName]             = useState('');
  const [email, setEmail]           = useState('');
  const [phone, setPhone]           = useState('');
  const [emailCode, setEmailCode]   = useState('');
  const [waCode, setWaCode]         = useState('');
  const [sending, setSending]       = useState(false);
  const [verifying, setVerify]      = useState(false);
  const [emailErr, setEmailErr]     = useState('');
  const [phoneErr, setPhoneErr]     = useState('');
  const [cooldown, setCooldown]     = useState(0);

  useEffect(() => { if (!open) { setStep(1); setEmailCode(''); setWaCode(''); setEmailErr(''); setPhoneErr(''); setCooldown(0); } }, [open]);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (!open) return null;

  const validateEmail = (v) => {
    if (!v.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim())) return 'Enter a valid email address';
    return '';
  };
  const validatePhone = (v) => {
    const digits = v.replace(/\D/g, '');
    if (!digits) return 'Phone number is required';
    if (digits.length < 10) return 'Phone must be at least 10 digits';
    if (digits.length > 13) return 'Phone number is too long';
    return '';
  };

  const sendOtp = async () => {
    const eErr = validateEmail(email);
    const pErr = validatePhone(phone);
    setEmailErr(eErr); setPhoneErr(pErr);
    if (eErr || pErr) return;
    setSending(true);
    try {
      const res = await fetch(`${PYTHON_BASE}/send-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), phone: phone.trim(), name: name.trim() }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed to send'); }
      toast.success(`OTP sent to ${email.trim()}`);
      setCooldown(60);
      setStep(2);
    } catch (e) { toast.error(e.message || 'Failed to send OTP'); }
    finally { setSending(false); }
  };

  const verifyOtp = async () => {
    if (!emailCode.trim()) return toast.error('Enter the 6-digit email OTP');
    if (!waCode.trim()) return toast.error('Enter the 6-digit WhatsApp OTP');
    setVerify(true);
    try {
      const res = await fetch(`${PYTHON_BASE}/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), phone: phone.trim(), email_code: emailCode.trim(), whatsapp_code: waCode.trim(), job_id: jobId || '' }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Invalid OTP'); }
      toast.success('Verified! Starting download…');
      onVerified(email.trim());
      onClose();
    } catch (e) { toast.error(e.message || 'Verification failed'); }
    finally { setVerify(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,20,34,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card" style={{ width: 460, padding: 36, boxShadow: 'var(--shadow-elegant)', position: 'relative', animation: 'fadeUp 0.25s ease' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-400)', display: 'flex', padding: 4 }}>
          <X size={18} />
        </button>

        {/* Brand */}
        <div style={{ marginBottom: 24 }}>
          <img src="/logo.png" alt="PR Audit AI Visibility" style={{ width: 183, height: 'auto' }} />
        </div>

        {step === 1 ? (
          <>
            <h2 className="font-display" style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Download Your Reports</h2>
            <p style={{ fontSize: 14, color: 'var(--ink-500)', lineHeight: 1.65, marginBottom: 24 }}>
              Enter your details — we'll send a 6-digit OTP to your <strong>email</strong> and <strong>WhatsApp</strong> to verify your identity.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-600)', display: 'block', marginBottom: 5 }}>Full Name</label>
                <input className="input" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-600)', display: 'block', marginBottom: 5 }}>Email Address</label>
                <div style={{ position: 'relative' }}>
                  <Mail size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: emailErr ? '#02008B' : 'var(--ink-400)' }} />
                  <input className="input" style={{ paddingLeft: 36, borderColor: emailErr ? '#02008B' : undefined }} type="email" placeholder="you@company.com"
                    value={email} onChange={e => { setEmail(e.target.value); setEmailErr(''); }} />
                </div>
                {emailErr && <p style={{ fontSize: 11, color: '#02008B', marginTop: 4 }}>{emailErr}</p>}
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-600)', display: 'block', marginBottom: 5 }}>WhatsApp Number</label>
                <div style={{ position: 'relative' }}>
                  <Phone size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: phoneErr ? '#02008B' : 'var(--ink-400)' }} />
                  <input className="input" style={{ paddingLeft: 36, borderColor: phoneErr ? '#02008B' : undefined }} type="tel" placeholder="+91 98765 43210"
                    value={phone} onChange={e => { setPhone(e.target.value); setPhoneErr(''); }} onKeyDown={e => e.key === 'Enter' && sendOtp()} autoComplete="tel" />
                </div>
                {phoneErr && <p style={{ fontSize: 11, color: '#02008B', marginTop: 4 }}>{phoneErr}</p>}
              </div>
              <button className="btn btn-primary" style={{ justifyContent: 'center', marginTop: 8 }} onClick={sendOtp} disabled={sending || cooldown > 0}>
                {sending
                  ? <><Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} />Sending OTP…</>
                  : cooldown > 0 ? `Resend available in ${cooldown}s`
                  : <><ShieldCheck size={15} />Send OTP to Email & WhatsApp</>}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ShieldCheck size={20} color="#16a34a" />
              </div>
              <div>
                <h2 className="font-display" style={{ fontSize: 20, fontWeight: 700, marginBottom: 2 }}>Enter your OTP</h2>
                <p style={{ fontSize: 13, color: 'var(--ink-500)' }}>Sent to <strong>{email}</strong> &amp; WhatsApp</p>
              </div>
            </div>
            <p style={{ fontSize: 14, color: 'var(--ink-500)', lineHeight: 1.65, marginBottom: 20 }}>
              Enter both codes below — one from your email inbox and one from WhatsApp. Check spam if the email doesn't arrive.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-600)', display: 'block', marginBottom: 5 }}>
                  <Mail size={12} style={{ display: 'inline', marginRight: 5, verticalAlign: 'middle' }} />
                  Email OTP
                </label>
                <input className="input" placeholder="— — — — — —"
                  value={emailCode} onChange={e => setEmailCode(e.target.value.replace(/\D/g,'').slice(0,6))}
                  style={{ fontSize: 24, letterSpacing: '0.3em', textAlign: 'center', fontWeight: 700 }}
                  autoFocus />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-600)', display: 'block', marginBottom: 5 }}>
                  <Phone size={12} style={{ display: 'inline', marginRight: 5, verticalAlign: 'middle' }} />
                  WhatsApp OTP
                </label>
                <input className="input" placeholder="— — — — — —"
                  value={waCode} onChange={e => setWaCode(e.target.value.replace(/\D/g,'').slice(0,6))}
                  style={{ fontSize: 24, letterSpacing: '0.3em', textAlign: 'center', fontWeight: 700 }}
                  onKeyDown={e => e.key === 'Enter' && verifyOtp()} />
              </div>
              <button className="btn btn-primary" style={{ justifyContent: 'center', marginTop: 4 }} onClick={verifyOtp} disabled={verifying || emailCode.length < 6 || waCode.length < 6}>
                {verifying ? <><Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} />Verifying…</> : <><ShieldCheck size={15} />Verify & Download</>}
              </button>
              <button className="btn btn-ghost btn-sm" style={{ justifyContent: 'center' }} onClick={() => { setStep(1); setEmailCode(''); setWaCode(''); }} disabled={cooldown > 0}>
                {cooldown > 0 ? `← Resend available in ${cooldown}s` : '← Change details / resend'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function MainPage() {
  const [company, setCompany]       = useState('');
  const [url, setUrl]               = useState('');
  const [running, setRunning]       = useState(false);
  const [jobId, setJobId]           = useState(null);
  const [progress, setProgress]     = useState(0);
  const [progressMsg, setProgressMsg] = useState('');
  const [audit, setAudit]           = useState(null);
  const [history, setHistory]       = useState([]);
  const [tab, setTab]               = useState('overview');
  const [openSection, setOpenSection] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  // OTP / verification
  const [verifiedEmail, setVerifiedEmail] = useState(null);
  const [otpOpen, setOtpOpen]       = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const progressRef = useRef(null);
  const resultsRef  = useRef(null);
  const navRef      = useRef(null);
  const canvasRef   = useRef(null);
  const heroRef     = useRef(null);
  const companyInputRef = useRef(null);
  const cardRef     = useRef(null);

  const { cursorRef, cursorTrailRef, scrollToHero, scrollToSection } = useLandingEffects({
    navRef, canvasRef, heroRef, companyInputRef,
  });

  useEffect(() => { setHistory(getHistory()); }, []);

  // Poll Python API while running
  useEffect(() => {
    if (!jobId || !running) return;
    let lastProgress = 0;
    const iv = setInterval(async () => {
      try {
        const res  = await fetch(`${PYTHON_BASE}/status/${jobId}`);
        const data = await res.json();
        const newPct = data.progress || 0;
        if (newPct >= lastProgress) {
          lastProgress = newPct;
          setProgress(newPct);
        }
        setProgressMsg(data.message || '');
        if (data.status === 'done') {
          clearInterval(iv);
          const r2     = await fetch(`${PYTHON_BASE}/result/${jobId}`);
          const result = await r2.json();
          setAudit(result);
          setRunning(false);
          saveToHistory(result);
          setHistory(getHistory());
          setTab('overview');
          setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        }
        if (data.status === 'failed') {
          clearInterval(iv);
          setRunning(false);
          toast.error(data.message || 'Audit failed.');
        }
      } catch {}
    }, 2500);
    return () => clearInterval(iv);
  }, [jobId, running]);

  const validateUrl = (v) => {
    const s = v.trim();
    if (!s) return 'Please enter a website URL';
    const withProto = /^https?:\/\//i.test(s) ? s : `https://${s}`;
    try {
      const u = new URL(withProto);
      if (!u.hostname.includes('.')) return 'Enter a valid URL (e.g. acme.com)';
      return '';
    } catch { return 'Enter a valid URL (e.g. acme.com)'; }
  };

  const run = async () => {
    const urlErr = validateUrl(url);
    if (urlErr) {
      if (cardRef.current) {
        cardRef.current.classList.add('shake');
        setTimeout(() => cardRef.current?.classList.remove('shake'), 500);
      }
      companyInputRef.current?.focus();
      return toast.error(urlErr);
    }
    setRunning(true);
    setProgress(0);
    setProgressMsg('Submitting…');
    setAudit(null);
    setTab('overview');
    setTimeout(() => progressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    try {
      const res  = await fetch(`${PYTHON_BASE}/generate`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ url: url.trim(), company_name: company.trim() || undefined }),
      });
      const data = await res.json();
      setJobId(data.job_id);
    } catch {
      setRunning(false);
      toast.error('Failed to reach Python API. Is it running on port 8000?');
    }
  };

  const _doDownloadZip = async () => {
    const res = await fetch(`${PYTHON_BASE}/download-zip/${jobId}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const safe = (audit?.company_name || '').replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${safe}_Reports.zip`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleDownloadAll = async () => {
    if (!verifiedEmail) { setPendingFile('__all__'); setOtpOpen(true); return; }
    if (downloadingAll || !jobId) return;
    setDownloadingAll(true);
    try { await _doDownloadZip(); toast.success('All reports downloaded!'); }
    catch (e) { toast.error(e.message || 'Download failed'); }
    finally { setDownloadingAll(false); }
  };

  const handleVerified = async (email) => {
    setVerifiedEmail(email);
    if (pendingFile === '__all__') {
      setDownloadingAll(true);
      try { await _doDownloadZip(); toast.success('All reports downloaded!'); }
      catch { toast.error('ZIP download failed'); }
      finally { setDownloadingAll(false); }
    } else if (pendingFile) {
      try { await _doDownload(jobId, pendingFile, audit?.company_name); toast.success('Downloaded!'); }
      catch (e) { toast.error(e.message || 'Download failed'); }
    }
    setPendingFile(null);
  };

  const openOtp = (file) => { setPendingFile(file); setOtpOpen(true); };

  const loadHistory = (entry) => {
    setAudit(entry);
    setJobId(entry.job_id);
    setTab('overview');
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const activeStep = Math.min(Math.floor((progress / 100) * PIPELINE_STEPS.length), PIPELINE_STEPS.length - 1);

  // Derived data for tabs
  const cp       = audit?.company_profile  || {};
  const vd       = audit?.visibility_data  || {};
  const sd       = audit?.seo_data         || {};
  const pd       = audit?.prompt_data      || {};
  const nd       = audit?.narratives_data  || {};
  const platforms = vd.platforms           || {};
  const scores   = audit?.scores           || {};

  const platformBarData = Object.entries(platforms).map(([name, p]) => ({
    name, total: p.total_score || 0,
    recognition: p.brand_recognition || 0,
    sentiment:   p.brand_sentiment   || 0,
    presence:    p.presence_quality  || 0,
    sov:         p.share_of_voice    || 0,
  }));
  const scoreRadar = [
    { subject: 'SEO',    A: sd.seo_score || 0 },
    { subject: 'AEO',    A: sd.aeo_score || 0 },
    { subject: 'GEO',    A: sd.geo_score || 0 },
    { subject: 'E-E-A-T',A: sd.eeat_pct  || 0 },
    { subject: 'AI Vis.',A: vd.overall_ai_visibility_score || 0 },
  ];
  const eeat     = sd.eeat_scores || {};
  const eeatData = [
    { name: 'Experience',       value: parseInt(eeat.experience)        || 0 },
    { name: 'Expertise',        value: parseInt(eeat.expertise)         || 0 },
    { name: 'Authoritativeness',value: parseInt(eeat.authoritativeness) || 0 },
    { name: 'Trustworthiness',  value: parseInt(eeat.trustworthiness)   || 0 },
  ];

  const TABS = [
    { id: 'overview',   label: 'Overview',        icon: <Star size={14} /> },
    { id: 'platforms',  label: 'AI Platforms',    icon: <Layers size={14} /> },
    { id: 'seo',        label: 'SEO / AEO / GEO', icon: <TrendingUp size={14} /> },
    { id: 'prompts',    label: 'Prompt Strategy', icon: <MessageSquare size={14} /> },
    { id: 'narratives', label: 'Narratives',       icon: <BookOpen size={14} /> },
    { id: 'downloads',  label: 'Downloads',        icon: <Download size={14} /> },
    { id: 'history',    label: `Recent (${history.length})`, icon: <RefreshCw size={14} /> },
  ];

  const LOADING_ICONS = ['🌐', '🤖', '📊', '⚡', '📝', '📈'];
  const step1Done = running || !!audit;
  const step2On = running;
  const step2Done = !!audit && !running;
  const step3On = !!audit && !running;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', fontFamily: 'var(--font-body)' }}>
      <div ref={cursorRef} className="pa-cursor" />
      <div ref={cursorTrailRef} className="pa-cursor-trail" />

      {/* Nav */}
      <nav ref={navRef} className="pa-nav">
        <button type="button" className="pa-nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <img src="/logo.png" alt="PR Audit AI Visibility" className="pa-nav-logo-img" />
        </button>
        <div className="pa-nav-links">
          <button type="button" className="pa-nav-lnk" onClick={() => scrollToSection('how')}>How it works</button>
          <button type="button" className="pa-nav-lnk" onClick={() => scrollToSection('features')}>Features</button>
          <button type="button" className="pa-nav-lnk" onClick={() => scrollToSection('stories')}>Stories</button>
          <button type="button" className="pa-nav-lnk" onClick={() => scrollToSection('faq')}>FAQ</button>
        </div>
        <button type="button" className="pa-nav-cta" onClick={scrollToHero}>Run Free Audit</button>
      </nav>

      {/* Hero — hidden once audit completes so results use full screen */}
      {(!audit || running) ? (
      <section ref={heroRef} id="hero" className="pa-hero">
        <canvas ref={canvasRef} className="pa-p-canvas" />
        <div className="pa-h-grid" />
        <div className="pa-h-vig" />
        <div className="pa-blob pa-b1" />
        <div className="pa-blob pa-b2" />
        <div className="pa-blob pa-b3" />
        <div className="pa-blob pa-b4" />

        <div className="pa-h-content">
          <div className="pa-h-ew">
            <div className="pa-ew-dot" />
            AI Visibility Intelligence — 2026
          </div>
          <h1 className="pa-hh">
            <span className="bold">Know</span> exactly<br />
            how <span className="accent">AI sees</span><br />
            your brand
          </h1>
          <p className="pa-h-sub">
            Get an instant SEO, AEO, GEO and AI Visibility score for any company. Download a precision report to dominate the AI search era — in under 2 minutes.
          </p>

          <div className="pa-a-arena" ref={progressRef}>
            <div ref={cardRef} className="pa-a-card">
              {!running ? (
                <div className="f-state">
                  <div className="pa-a-topbar">
                    <div className="pa-a-badge"><div className="pa-a-bdot" />Free Audit</div>
                    <div className="pa-a-free">No signup · No credit card · <b>100% Free</b></div>
                  </div>
                  <div className="pa-ap">
                    <div className={`pa-aps ${!step1Done ? 'on' : 'done'}`}><div className="pa-apn">1</div>Details</div>
                    <div className={`pa-apl ${step1Done ? 'done' : ''}`} />
                    <div className={`pa-aps ${step2On ? 'on' : ''} ${step2Done ? 'done' : ''}`}><div className="pa-apn">2</div>Scanning</div>
                    <div className={`pa-apl ${step2Done ? 'done' : ''}`} />
                    <div className={`pa-aps ${step3On ? 'on done' : ''}`}><div className="pa-apn">3</div>Report</div>
                  </div>
                  <div className="pa-fg full">
                    <div className="pa-fld">
                      <div className="pa-fl">Company Name</div>
                      <div className="pa-fw">
                        <input
                          ref={companyInputRef}
                          className="pa-fi"
                          type="text"
                          placeholder="e.g. Acme Corporation"
                          autoComplete="organization"
                          value={company}
                          onChange={e => setCompany(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && run()}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="pa-fg full">
                    <div className="pa-fld">
                      <div className="pa-fl">Website URL</div>
                      <div className="pa-fw">
                        <span className="pa-fp">https://</span>
                        <input
                          className="pa-fi"
                          type="text"
                          placeholder="yourcompany.com"
                          value={url}
                          onChange={e => setUrl(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && run()}
                        />
                      </div>
                    </div>
                  </div>
                  <button type="button" className="pa-rb" onClick={run}>
                    <span>Generate My AI Visibility Report</span>
                    <div className="pa-rb-arr">
                      <svg width="13" height="13" fill="white" viewBox="0 0 24 24"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                    </div>
                  </button>
                  <div className="pa-tr">
                    <div className="pa-ti"><div className="pa-tp" style={{ background: 'var(--indigo)' }} />Results in ~2 min</div>
                    <div className="pa-ti"><div className="pa-tp" style={{ background: 'var(--teal)' }} />Real crawled data</div>
                    <div className="pa-ti"><div className="pa-tp" style={{ background: 'var(--saffron)' }} />Free PDF download</div>
                    <div className="pa-ti"><div className="pa-tp" style={{ background: 'var(--rose)' }} />10,000+ brands</div>
                  </div>
                </div>
              ) : (
                <div className="pa-ls">
                  <div className="pa-rw">
                    <svg width="76" height="76" viewBox="0 0 76 76">
                      <circle cx="38" cy="38" r="32" fill="none" stroke="var(--border2)" strokeWidth="5" />
                      <circle cx="38" cy="38" r="32" fill="none" stroke="url(#pa-lg)" strokeWidth="5" strokeDasharray="201" strokeDashoffset={201 * (1 - progress / 100)} strokeLinecap="round" transform="rotate(-90 38 38)" />
                      <defs>
                        <linearGradient id="pa-lg" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#3D1FCC" />
                          <stop offset="100%" stopColor="#D42F5C" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="pa-rp">{Math.round(progress)}%</div>
                  </div>
                  <div className="pa-lm">{progressMsg || 'Initializing scan engine…'}</div>
                  <div className="pa-lsl">
                    {PIPELINE_STEPS.map((step, i) => {
                      const done = i < activeStep;
                      const on = i === activeStep;
                      return (
                        <div key={i} className={`pa-lsi ${on ? 'on' : ''} ${done ? 'done' : ''}`}>
                          <div className="pa-li">{LOADING_ICONS[i] || '⚡'}</div>
                          {step}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {!running && (
            <div className="pa-sh">
              <div className="pa-st">scroll to explore</div>
              <div className="pa-sl" />
            </div>
          )}
        </div>
      </section>
      ) : (
        <div ref={heroRef} id="hero" className="pa-hero-anchor" />
      )}

      {/* Landing sections (shown when no results or below hero) */}
      {!audit && <LandingSections onCtaClick={scrollToHero} />}

      {/* ── Results ──────────────────────────────────────────────────── */}
      {audit && (
        <section ref={resultsRef} className="pa-results-wrap section-outer anim-up">
          <div className="pa-results-inner">

          {/* Result header */}
          <div className="result-header">
            <div>
              <div className="result-eyebrow">Audit results</div>
              <h2 className="result-title font-display">
                {cp.company_name || audit.company_name || 'Results'}
              </h2>
              <div className="result-meta">
                {(cp.domain || audit.domain) && (
                  <span className="result-meta-item">
                    <Globe size={13} />{cp.domain || audit.domain}
                  </span>
                )}
                {cp.industry && <span className="badge badge-blue" style={{ fontSize: 11 }}>{cp.industry}</span>}
                {cp.country  && <span className="badge badge-gray" style={{ fontSize: 11 }}>📍 {cp.country}</span>}
                {audit.duration_s && <span className="result-meta-time">Generated in {audit.duration_s}s</span>}
              </div>
            </div>
            <div className="result-actions">
              <button onClick={() => setTab('downloads')} className="btn btn-primary btn-sm">
                <Download size={14} /> Download Action Items
              </button>
            </div>
          </div>

          {/* Score hero card */}
          <div className="card hero-score-card">
            <div className="hero-score-layout">
              <div className="hero-score-ring">
                <ScoreRing value={vd.overall_ai_visibility_score || 0} size={160} stroke={12} />
                <div className="hero-score-ring-label font-display">AI Visibility</div>
                {vd.overall_grade && (
                  <div className="hero-grade-row">
                    <span className={`badge ${gradeBadgeClass(vd.overall_grade)}`}>{vd.overall_grade}</span>
                  </div>
                )}
              </div>
              <div className="hero-score-metrics">
                <div className="hero-metrics-row">
                  {[
                    { label: 'SEO', val: sd.seo_score    || 0, color: '#16a34a', bg: '#f0fdf4', icon: <TrendingUp size={15} /> },
                    { label: 'AEO', val: sd.aeo_score    || 0, color: '#0891b2', bg: '#e0f2fe', icon: <Zap size={15} /> },
                    { label: 'GEO', val: sd.geo_score    || 0, color: '#7c3aed', bg: '#ede9fe', icon: <Globe size={15} /> },
                    { label: 'E-E-A-T', val: sd.eeat_pct  || 0, color: '#d97706', bg: '#fffbeb', icon: <Star size={15} /> },
                    { label: 'Overall', val: sd.overall_score || 0, color: 'var(--indigo)', bg: '#fff1f0', icon: <CheckCircle2 size={15} /> },
                  ].map((m, i) => (
                    <div key={i} className="hero-metric-card hover-lift" style={{ background: m.bg }}>
                      <div className="hero-metric-head">
                        <span className="hero-metric-icon" style={{ color: m.color }}>{m.icon}</span>
                        <span className="hero-metric-label">{m.label}</span>
                      </div>
                      <div className="hero-metric-value font-display" style={{ color: m.color }}>{m.val}</div>
                      <div className="hero-metric-bar">
                        <div className="hero-metric-fill" style={{ width: `${m.val}%`, background: m.color }} />
                      </div>
                    </div>
                  ))}
                </div>
                {(vd.platform_ranking || []).length > 0 && (
                  <div className="hero-platform-ranking">
                    <span className="hero-platform-label">Platform ranking:</span>
                    {(vd.platform_ranking || []).map((p, i) => (
                      <div key={p} className="hero-platform-pill">
                        <span className="hero-platform-rank">#{i + 1}</span>
                        <PlatformLogo name={p} size={18} />
                        <span className="hero-platform-name">{p}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tab nav */}
          <div className="tab-nav" style={{ display: 'flex', gap: 4, background: 'var(--surface)', borderRadius: 'var(--r-lg)', padding: 5, border: '1px solid var(--border)', maxWidth: '100%', overflowX: 'auto', marginBottom: 20 }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                borderRadius: 'var(--r-md)', border: 'none', cursor: 'pointer',
                fontFamily: 'var(--font-body)', fontSize: 13,
                fontWeight: tab === t.id ? 700 : 500,
                background: tab === t.id ? 'var(--indigo)' : 'transparent',
                color: tab === t.id ? 'white' : 'var(--ink-500)',
                transition: 'var(--t)', whiteSpace: 'nowrap',
              }}>
                {t.icon}{t.label}
              </button>
            ))}
          </div>

          {/* ── OVERVIEW TAB ─────────────────────────────────────────── */}
          {tab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }} className="anim-in">
              <div className="card card-pad">
                <div className="section-title"><Info size={16} color="var(--red-600)" />Company Intelligence</div>
                <div className="grid-2col" style={{ gap: 24 }}>
                  <div>
                    <p style={{ fontSize: 14, color: 'var(--ink-600)', lineHeight: 1.8, marginBottom: 16 }}>
                      {cp.company_description_long || cp.company_description_short || '—'}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {(cp.unique_value_propositions || []).map((uvp, i) => (
                        <span key={i} className="badge badge-blue" style={{ fontSize: 11 }}>✓ {uvp}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      { label: 'Industry', val: cp.industry },
                      { label: 'Country', val: cp.country },
                      { label: 'Company Size', val: cp.company_size },
                      { label: 'Geographic Focus', val: cp.geographic_focus },
                      { label: 'Brand Tone', val: cp.brand_tone },
                    ].map((r, i) => r.val && (
                      <div key={i} style={{ display: 'flex', gap: 10, fontSize: 13, borderBottom: '1px solid var(--ink-50)', paddingBottom: 8 }}>
                        <span style={{ width: 130, color: 'var(--ink-400)', flexShrink: 0 }}>{r.label}</span>
                        <span style={{ fontWeight: 600 }}>{r.val}</span>
                      </div>
                    ))}
                    <div style={{ marginTop: 4 }}>
                      <div style={{ fontSize: 12, color: 'var(--ink-400)', marginBottom: 6 }}>Competitors</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {(cp.key_competitors || []).map((c, i) => (
                          <span key={i} className="badge badge-gray" style={{ fontSize: 11 }}>{c}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid-2col" style={{ gap: 20 }}>
                <div className="card card-pad">
                  <div className="section-title"><Star size={16} color="var(--red-600)" />Score Radar</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <RadarChart data={scoreRadar}>
                      <PolarGrid stroke="var(--border)" />
                      <PolarAngleAxis dataKey="subject" tick={{ fontFamily: 'Onest', fontSize: 12, fill: 'var(--ink-500)' }} />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                      <Radar dataKey="A" stroke="#02008B" fill="#02008B" fillOpacity={0.15} strokeWidth={2.5} dot={{ r: 4, fill: '#02008B' }} />
                      <Tooltip contentStyle={{ fontFamily: 'Onest', fontSize: 12, borderRadius: 8, border: '1px solid var(--border)' }} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                <div className="card card-pad">
                  <div className="section-title"><CheckCircle2 size={16} color="var(--red-600)" />E-E-A-T Analysis</div>
                  <div className="grid-2col" style={{ gap: 12, marginBottom: 16 }}>
                    {eeatData.map((d, i) => (
                      <div key={i} style={{ padding: 14, background: 'var(--ink-50)', borderRadius: 'var(--r-md)', textAlign: 'center' }}>
                        <div style={{ fontSize: 26, fontWeight: 800, color: sc(d.value), marginBottom: 4 }}>{d.value}</div>
                        <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>{d.name}</div>
                        <div className="progress-bar" style={{ marginTop: 8, height: 5 }}>
                          <div className="progress-fill" style={{ width: `${d.value}%`, background: sc(d.value) }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--ink-500)', lineHeight: 1.6 }}>{sd.eeat_analysis || '—'}</p>
                </div>
              </div>

              <div className="grid-2col" style={{ gap: 20 }}>
                <div className="card card-pad">
                  <div className="section-title"><AlertTriangle size={16} color="#d97706" />Top Visibility Gaps</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {(vd.top_visibility_gaps || []).map((g, i) => (
                      <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 12px', background: '#fffbeb', borderRadius: 8, border: '1px solid #fef3c7' }}>
                        <XCircle size={15} color="#d97706" style={{ flexShrink: 0, marginTop: 1 }} />
                        <span style={{ fontSize: 13, color: '#78350f' }}>{renderSafeValue(g)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="card card-pad">
                  <div className="section-title"><Zap size={16} color="#16a34a" />Quick Wins</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {(vd.score_improvement_roadmap?.quick_wins || sd.quick_wins || []).slice(0, 6).map((w, i) => (
                      <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 12px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #d1fae5' }}>
                        <CheckCircle2 size={15} color="#16a34a" style={{ flexShrink: 0, marginTop: 1 }} />
                        <span style={{ fontSize: 13, color: '#14532d' }}>{renderSafeValue(w)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── AI PLATFORMS TAB ─────────────────────────────────────── */}
          {tab === 'platforms' && (
            <BlurGate unlocked={!!verifiedEmail} onUnlock={() => openOtp('__all__')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }} className="anim-in">
              <div className="card card-pad">
                <div className="section-title"><Layers size={16} color="var(--red-600)" />Platform Score Comparison</div>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={platformBarData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontFamily: 'Onest', fontSize: 13, fill: 'var(--ink-600)' }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fontFamily: 'Onest', fontSize: 11, fill: 'var(--ink-400)' }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ fontFamily: 'Onest', fontSize: 12, borderRadius: 8, border: '1px solid var(--border)' }} />
                    <Bar dataKey="total" name="Total Score" radius={[8, 8, 0, 0]} maxBarSize={70}>
                      {platformBarData.map(d => <Cell key={d.name} fill={PLATFORM_META[d.name]?.color || '#6b7280'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="grid-2col" style={{ gap: 20 }}>
                {Object.entries(platforms).map(([name, p]) => {
                  const meta = PLATFORM_META[name] || { color: '#6b7280', bg: '#f9fafb' };
                  return (
                    <div key={name} className="card" style={{ overflow: 'hidden' }}>
                      <div style={{ padding: '16px 20px', background: meta.bg, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
                        <PlatformLogo name={name} size={32} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 800, fontSize: 16 }}>{name}</div>
                          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                            <span className={`badge ${gradeBadgeClass(p.grade)}`}>{p.grade}</span>
                            <span className={`badge ${statusBadge(p.status)}`}>{p.status}</span>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 40, fontWeight: 900, color: meta.color, lineHeight: 1 }}>{p.total_score}</div>
                          <div style={{ fontSize: 11, color: 'var(--ink-400)' }}>/100</div>
                        </div>
                      </div>
                      <div style={{ padding: '16px 20px' }}>
                        <ScoreBar label="Brand Recognition" value={p.brand_recognition || 0} max={20} />
                        <ScoreBar label="Market Score"      value={p.market_score || 0}      max={10} />
                        <ScoreBar label="Presence Quality"  value={p.presence_quality || 0}  max={20} />
                        <ScoreBar label="Brand Sentiment"   value={p.brand_sentiment || 0}   max={40} />
                        <ScoreBar label="Share of Voice"    value={p.share_of_voice || 0}    max={10} />
                        {p.scoring_rationale && (
                          <p style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 12, lineHeight: 1.6, borderTop: '1px solid var(--ink-50)', paddingTop: 12 }}>
                            {p.scoring_rationale}
                          </p>
                        )}
                        {p.strengths?.length > 0 && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Strengths</div>
                            {p.strengths.map((s, i) => (
                              <div key={i} style={{ display: 'flex', gap: 6, fontSize: 12, color: 'var(--ink-700)', marginBottom: 4 }}>
                                <CheckCircle2 size={13} color="#16a34a" style={{ flexShrink: 0, marginTop: 1 }} />{s}
                              </div>
                            ))}
                          </div>
                        )}
                        {p.gaps?.length > 0 && (
                          <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--red-600)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Gaps</div>
                            {p.gaps.map((g, i) => (
                              <div key={i} style={{ display: 'flex', gap: 6, fontSize: 12, color: 'var(--ink-700)', marginBottom: 4 }}>
                                <XCircle size={13} color="var(--red-600)" style={{ flexShrink: 0, marginTop: 1 }} />{g}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {vd.score_improvement_roadmap && (
                <div className="card card-pad">
                  <div className="section-title"><TrendingUp size={16} color="var(--red-600)" />Score Improvement Roadmap</div>
                  <div className="grid-3col" style={{ gap: 20 }}>
                    {[
                      { key: 'quick_wins',  label: '⚡ Quick Wins',   color: '#16a34a', bg: '#f0fdf4' },
                      { key: 'medium_term', label: '📈 Medium Term',  color: '#d97706', bg: '#fffbeb' },
                      { key: 'long_term',   label: '🎯 Long Term',    color: '#7c3aed', bg: '#ede9fe' },
                    ].map(({ key, label, color, bg }) => (
                      <div key={key} style={{ padding: 16, background: bg, borderRadius: 'var(--r-md)' }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color, marginBottom: 10 }}>{label}</div>
                        {(vd.score_improvement_roadmap[key] || []).map((a, i) => (
                          <div key={i} style={{ fontSize: 12, color: 'var(--ink-700)', marginBottom: 7, display: 'flex', gap: 6 }}>
                            <span style={{ color, flexShrink: 0 }}>→</span>{a}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            </BlurGate>
          )}

          {/* ── SEO / AEO / GEO TAB ──────────────────────────────────── */}
          {tab === 'seo' && (
            <BlurGate unlocked={!!verifiedEmail} onUnlock={() => openOtp('__all__')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }} className="anim-in">
              <div className="grid-4col" style={{ gap: 16 }}>
                {[
                  { label: 'SEO Score', val: sd.seo_score || 0, sub: 'Search Engine' },
                  { label: 'AEO Score', val: sd.aeo_score || 0, sub: 'Answer Engine' },
                  { label: 'GEO Score', val: sd.geo_score || 0, sub: 'Generative Engine' },
                  { label: 'E-E-A-T',   val: sd.eeat_pct  || 0, sub: 'Experience · Expertise' },
                ].map((m, i) => (
                  <div key={i} className="card card-pad" style={{ textAlign: 'center' }}>
                    <ScoreRing value={m.val} size={90} stroke={7} />
                    <div style={{ fontWeight: 700, fontSize: 14, marginTop: 10 }}>{m.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-400)', marginTop: 2 }}>{m.sub}</div>
                  </div>
                ))}
              </div>

              {sd.seo_breakdown?.length > 0 && (
                <div className="card" style={{ overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}><div className="section-title" style={{ marginBottom: 0 }}>SEO Breakdown</div></div>
                  <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table className="data-table">
                    <thead><tr><th>Component</th><th>Score</th><th>Status</th></tr></thead>
                    <tbody>{sd.seo_breakdown.map((row, i) => <tr key={i}><td style={{ fontWeight: 500 }}>{row[0]}</td><td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{row[1]}</td><td><StatusPill val={row[2]} /></td></tr>)}</tbody>
                  </table>
                  </div>
                </div>
              )}
              {sd.aeo_components?.length > 0 && (
                <div className="card" style={{ overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}><div className="section-title" style={{ marginBottom: 0 }}>AEO Component Analysis</div></div>
                  <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table className="data-table">
                    <thead><tr><th>AEO Component</th><th>Finding</th><th>Status</th></tr></thead>
                    <tbody>{sd.aeo_components.map((row, i) => <tr key={i}><td style={{ fontWeight: 500 }}>{row[0]}</td><td style={{ color: 'var(--ink-600)', fontSize: 13, whiteSpace: 'nowrap' }}>{row[1]}</td><td><StatusPill val={row[2]} /></td></tr>)}</tbody>
                  </table>
                  </div>
                </div>
              )}
              {sd.geo_components?.length > 0 && (
                <div className="card" style={{ overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}><div className="section-title" style={{ marginBottom: 0 }}>GEO Component Analysis</div></div>
                  <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table className="data-table">
                    <thead><tr><th>GEO Component</th><th>Finding</th><th>Status</th></tr></thead>
                    <tbody>{sd.geo_components.map((row, i) => <tr key={i}><td style={{ fontWeight: 500 }}>{row[0]}</td><td style={{ color: 'var(--ink-600)', fontSize: 13, whiteSpace: 'nowrap' }}>{row[1]}</td><td><StatusPill val={row[2]} /></td></tr>)}</tbody>
                  </table>
                  </div>
                </div>
              )}
              {sd.seo_opportunities?.length > 0 && (
                <div className="card" style={{ overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}><div className="section-title" style={{ marginBottom: 0 }}>SEO Opportunities</div></div>
                  <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table className="data-table">
                    <thead><tr><th>Category</th><th>Recommendation</th><th>Priority</th><th>Impact</th></tr></thead>
                    <tbody>{sd.seo_opportunities.map((row, i) => (
                      <tr key={i}>
                        <td><span className="badge badge-blue" style={{ fontSize: 11 }}>{row[0]}</span></td>
                        <td style={{ fontSize: 13, maxWidth: 320 }}>{row[1]}</td>
                        <td><span className={`badge ${row[2] === 'HIGH' || row[2] === '🔴 Critical' ? 'badge-red' : row[2] === 'MEDIUM' || row[2] === '🟠 High' ? 'badge-amber' : 'badge-green'}`} style={{ fontSize: 11 }}>{row[2]}</span></td>
                        <td style={{ fontWeight: 700, color: '#16a34a', fontSize: 13 }}>{row[3]}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                  </div>
                </div>
              )}
              {sd.critical_issues?.length > 0 && (
                <div className="card card-pad">
                  <div className="section-title"><XCircle size={16} color="var(--red-600)" />Critical Issues</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {sd.critical_issues.map((issue, i) => (
                      <div key={i} style={{ display: 'flex', gap: 10, padding: '12px 14px', background: 'var(--red-50)', borderRadius: 8, border: '1px solid var(--red-100)' }}>
                        <XCircle size={15} color="var(--red-600)" style={{ flexShrink: 0, marginTop: 1 }} />
                        <span style={{ fontSize: 13, color: 'var(--red-800)' }}>{issue}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            </BlurGate>
          )}

          {/* ── PROMPT STRATEGY TAB ──────────────────────────────────── */}
          {tab === 'prompts' && (
            <BlurGate unlocked={!!verifiedEmail} onUnlock={() => openOtp('__all__')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }} className="anim-in">
              {pd.summary && (
                <div className="card card-pad" style={{ background: 'linear-gradient(135deg,var(--ink-50),white)' }}>
                  <div className="section-title"><MessageSquare size={16} color="var(--red-600)" />Prompt Strategy Summary</div>
                  <p style={{ fontSize: 14, color: 'var(--ink-600)', lineHeight: 1.8 }}>{pd.summary}</p>
                </div>
              )}
              {pd.query_categories && Object.keys(pd.query_categories).length > 0 && (
                <div className="card" style={{ overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
                    <div className="section-title" style={{ marginBottom: 0 }}>
                      AI Query Categories
                      <span className="badge badge-blue" style={{ marginLeft: 8, fontSize: 11 }}>{Object.values(pd.query_categories).flat().length} total queries</span>
                    </div>
                  </div>
                  <div style={{ padding: 20 }}>
                    {Object.entries(pd.query_categories).map(([cat, queries]) => (
                      <div key={cat} style={{ marginBottom: 20 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--red-600)', marginBottom: 8, padding: '4px 10px', background: 'var(--red-50)', borderRadius: 6, display: 'inline-block' }}>{cat}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {(Array.isArray(queries) ? queries : []).map((q, i) => (
                            <div key={i} style={{ fontSize: 12, padding: '5px 10px', background: 'var(--ink-50)', borderRadius: 6, color: 'var(--ink-700)', border: '1px solid var(--border)' }}>"{renderSafeValue(q)}"</div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {pd.optimisation_recommendations?.length > 0 && (
                <div className="card card-pad">
                  <div className="section-title">Optimisation Recommendations</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {pd.optimisation_recommendations.map((rec, i) => (
                      <div key={i} style={{ display: 'flex', gap: 10, padding: '12px 14px', background: 'var(--ink-50)', borderRadius: 8, border: '1px solid var(--border)' }}>
                        <span style={{ color: 'var(--red-600)', fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
                        <span style={{ fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.6 }}>{renderSafeValue(rec)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {pd.competitive_positioning && (
                <div className="card card-pad" style={{ background: 'var(--ink-50)' }}>
                  <div className="section-title">Competitive Positioning</div>
                  <p style={{ fontSize: 14, color: 'var(--ink-600)', lineHeight: 1.8 }}>{renderSafeValue(pd.competitive_positioning)}</p>
                </div>
              )}
            </div>
            </BlurGate>
          )}

          {/* ── NARRATIVES TAB ───────────────────────────────────────── */}
          {tab === 'narratives' && (
            <BlurGate unlocked={!!verifiedEmail} onUnlock={() => openOtp('__all__')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }} className="anim-in">
              {nd.content_cluster_strategy && (
                <div className="card card-pad">
                  <div className="section-title"><BookOpen size={16} color="var(--red-600)" />Content Cluster Strategy</div>
                  <p style={{ fontSize: 14, color: 'var(--ink-600)', lineHeight: 1.8 }}>{nd.content_cluster_strategy}</p>
                </div>
              )}
              {(nd.narratives || []).map((n, i) => (
                <div key={i} className="card" style={{ overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', background: 'var(--ink-50)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                      <span className="badge badge-red" style={{ fontSize: 11, marginBottom: 8, display: 'inline-flex' }}>{n.type}</span>
                      <div style={{ fontWeight: 800, fontSize: 16 }}>{n.title}</div>
                      {n.subtitle && <div style={{ fontSize: 13, color: 'var(--ink-500)', marginTop: 3 }}>{n.subtitle}</div>}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <span className="badge badge-green" style={{ fontSize: 11 }}>{n.publication_priority || 'High'} Priority</span>
                      <div style={{ fontSize: 11, color: 'var(--ink-400)', marginTop: 4 }}>~{n.word_count || '?'} words</div>
                    </div>
                  </div>
                  <div style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                      {(n.target_keywords || []).map((kw, j) => (
                        <span key={j} className="badge badge-violet" style={{ fontSize: 11 }}>🔑 {kw}</span>
                      ))}
                    </div>
                    {n.body && (
                      <div>
                        <button onClick={() => setOpenSection(openSection === `n-${i}` ? null : `n-${i}`)}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Onest,sans-serif', fontWeight: 600, fontSize: 13, color: 'var(--red-600)', padding: '0 0 12px', transition: 'var(--t)' }}>
                          {openSection === `n-${i}` ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                          {openSection === `n-${i}` ? 'Collapse' : 'Read Full Narrative'}
                        </button>
                        {openSection === `n-${i}` && (
                          <div style={{ maxHeight: 500, overflowY: 'auto', padding: 16, background: 'var(--ink-50)', borderRadius: 'var(--r-md)', fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.9, whiteSpace: 'pre-wrap', animation: 'fadeIn 0.2s ease' }}>
                            {n.body}
                          </div>
                        )}
                      </div>
                    )}
                    {n.ai_optimization_notes && (
                      <div style={{ marginTop: 12, padding: '10px 14px', background: '#ede9fe', borderRadius: 8, border: '1px solid #c4b5fd' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#5b21b6' }}>AI Optimization: </span>
                        <span style={{ fontSize: 12, color: '#4c1d95' }}>{n.ai_optimization_notes}</span>
                      </div>
                    )}
                    {n.schema_recommendation && (
                      <div style={{ marginTop: 8, padding: '10px 14px', background: '#dbeafe', borderRadius: 8, border: '1px solid #93c5fd' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8' }}>Schema: </span>
                        <span style={{ fontSize: 12, color: '#1e3a8a' }}>{n.schema_recommendation}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {nd.distribution_plan && (
                <div className="card card-pad">
                  <div className="section-title">Distribution Plan</div>
                  <p style={{ fontSize: 14, color: 'var(--ink-600)', lineHeight: 1.8 }}>{nd.distribution_plan}</p>
                </div>
              )}
              {nd.expected_impact && (
                <div className="card card-pad" style={{ background: 'linear-gradient(135deg,#f0fdf4,white)', border: '1px solid #d1fae5' }}>
                  <div className="section-title" style={{ color: '#15803d' }}><TrendingUp size={16} color="#16a34a" />Expected Impact</div>
                  <p style={{ fontSize: 14, color: '#166534', lineHeight: 1.8 }}>{nd.expected_impact}</p>
                </div>
              )}
            </div>
            </BlurGate>
          )}

          {/* ── DOWNLOADS TAB ────────────────────────────────────────── */}
          {tab === 'downloads' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} className="anim-in">
              <div className="card card-pad">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                  <div className="section-title" style={{ marginBottom: 0 }}><Download size={16} color="var(--red-600)" />Generated Report Files</div>
                  {audit.files?.length > 0 && (
                    <button onClick={handleDownloadAll} disabled={downloadingAll} className="btn btn-primary btn-sm">
                      {downloadingAll
                        ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />Downloading All…</>
                        : verifiedEmail
                          ? <><Download size={14} />Download All</>
                          : <><Lock size={14} />Verify to Download All</>}
                    </button>
                  )}
                </div>
                {audit.files?.length > 0 ? (
                  <div className="grid-2col" style={{ gap: 16 }}>
                    {[
                      { file: 'AI_Visibility_Report.pdf', type: 'PDF', title: 'AI Visibility Report',   desc: 'Platform scores, E-E-A-T, gaps and improvement roadmap', color: '#02008B', bg: '#fff1f0' },
                      { file: 'Prompt_Report.pdf',        type: 'PDF', title: 'Prompt Strategy Report', desc: 'AI query categories and optimisation plan',              color: '#7c3aed', bg: '#ede9fe' },
                      { file: 'Narratives.pdf',           type: 'PDF', title: 'AI Narratives',          desc: '3 ready-to-publish AI-optimised articles',              color: '#0891b2', bg: '#e0f2fe' },
                      { file: 'Task_Tracker.xlsx',        type: 'XLSX',title: 'Task Tracker',           desc: 'SEO, AEO, GEO & E-E-A-T prioritised sprint tasks',      color: '#16a34a', bg: '#f0fdf4' },
                    ].map(({ file, type, title, desc, color, bg }) => {
                      const available = audit.files.includes(file);
                      return (
                        <div key={file} className="card" style={{ padding: 20, opacity: available ? 1 : 0.5, border: available ? '1px solid var(--border)' : '1px dashed var(--border)' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                            <div style={{ width: 44, height: 44, borderRadius: 'var(--r-md)', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 11, fontWeight: 800, color }}>
                              {type}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{title}</div>
                              <div style={{ fontSize: 12, color: 'var(--ink-400)', marginBottom: 14, lineHeight: 1.5 }}>{desc}</div>
                              {available
                                ? <DownloadBtn jobId={jobId} file={file} verifiedEmail={verifiedEmail} onNeedVerify={openOtp} companyName={audit?.company_name} />
                                : <span className="badge badge-gray" style={{ fontSize: 11 }}>Not available</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--ink-400)' }}>
                    <FileText size={36} style={{ marginBottom: 12, opacity: 0.3 }} />
                    <p style={{ fontSize: 14 }}>No files available. Make sure the audit completed successfully.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── HISTORY TAB ──────────────────────────────────────────── */}
          {tab === 'history' && (
            <div className="anim-in">
              {history.length === 0 ? (
                <div className="card card-pad" style={{ textAlign: 'center', color: 'var(--ink-400)', padding: '48px 20px' }}>
                  <RefreshCw size={36} style={{ marginBottom: 12, opacity: 0.3 }} />
                  <p>No previous audits yet. Run your first audit above.</p>
                </div>
              ) : (
                <div className="card" style={{ overflow: 'hidden' }}>
                  <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table className="data-table" style={{ minWidth: 560 }}>
                    <thead>
                      <tr><th>Company</th><th>Domain</th><th>AI Vis.</th><th>SEO</th><th>Overall</th><th>When</th></tr>
                    </thead>
                    <tbody>
                      {history.map(h => (
                        <tr key={h.job_id} style={{ cursor: 'pointer' }} onClick={() => loadHistory(h)}>
                          <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{h.company_name || '—'}</td>
                          <td style={{ color: 'var(--ink-400)', fontSize: 13, whiteSpace: 'nowrap' }}>{h.domain || '—'}</td>
                          <td><span style={{ fontWeight: 700, color: sc(h.scores?.ai_visibility || 0) }}>{h.scores?.ai_visibility || 0}</span></td>
                          <td><span style={{ fontWeight: 700, color: sc(h.scores?.seo || 0) }}>{h.scores?.seo || 0}</span></td>
                          <td><span style={{ fontWeight: 700, color: sc(h.scores?.overall || 0) }}>{h.scores?.overall || 0}</span></td>
                          <td style={{ color: 'var(--ink-400)', fontSize: 13, whiteSpace: 'nowrap' }}>{new Date(h.timestamp).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
        </section>
      )}

      {audit && <LandingSections onCtaClick={scrollToHero} />}

      {/* ── OTP Modal ────────────────────────────────────────────────── */}
      <OtpModal
        open={otpOpen}
        onClose={() => setOtpOpen(false)}
        jobId={jobId}
        onVerified={handleVerified}
      />

      <footer className="pa-footer">
        <div className="pa-ftop">
          <div>
            <img src="/logo.png" alt="PR Audit AI Visibility" className="pa-footer-logo" />
            <p className="pa-fdesc">Helping brands understand and improve their visibility in the age of AI. Free audits, actionable intelligence, real results.</p>
          </div>
          <div className="pa-fcol">
            <div className="pa-fct">Product</div>
            <ul>
              <li><button type="button" className="pa-nav-lnk" style={{ padding: 0, color: 'rgba(255,255,255,0.32)' }} onClick={() => scrollToSection('features')}>Features</button></li>
              <li><button type="button" className="pa-nav-lnk" style={{ padding: 0, color: 'rgba(255,255,255,0.32)' }} onClick={() => scrollToSection('how')}>How it works</button></li>
            </ul>
          </div>
          <div className="pa-fcol">
            <div className="pa-fct">Resources</div>
            <ul>
              <li><a href="#faq" onClick={e => { e.preventDefault(); scrollToSection('faq'); }}>AI Visibility Guide</a></li>
              <li><a href="#faq" onClick={e => { e.preventDefault(); scrollToSection('faq'); }}>SEO vs AEO vs GEO</a></li>
            </ul>
          </div>
          <div className="pa-fcol">
            <div className="pa-fct">Company</div>
            <ul>
              <li><a href="#hero" onClick={e => { e.preventDefault(); scrollToHero(); }}>Contact</a></li>
            </ul>
          </div>
        </div>
        <div className="pa-fbot">
          <span>© {new Date().getFullYear()} PR AUDIT · ALL RIGHTS RESERVED</span>
          <div className="pa-flinks">
            <a href="#faq" onClick={e => { e.preventDefault(); scrollToSection('faq'); }}>Privacy</a>
            <a href="#faq" onClick={e => { e.preventDefault(); scrollToSection('faq'); }}>Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
